import { gql, TypedDocumentNode } from "@apollo/client";

export type PersonTimelineQueryVariables = {
  pgaId: string;
};

export type PersonTimelineData = {
  person: TimelinePerson;
};

export type TimelinePerson = {
  id: string;
  pgaId?: string | null;
  accountCreatedAt?: string | null;
  bands?: TimelineBand[] | null;
  sectionAffiliations?: TimelineSectionAffiliation[] | null;
  employments?: TimelineEmployment[] | null;
  ccmsSuspensions?: TimelineCcmsSuspension[] | null;
  prospectProgram?: TimelineProspectProgram | null;
  educationLatestCertification?: TimelineEducationCertification | null;
  educationPgmProgramHistory?: TimelineEducationPgmProgram[] | null;
};

export type TimelineBand = {
  id: string;
  createdReason?: string | null;
  terminatedReason?: string | null;
  expiresOn?: string | null;
  startedOn: string;
  terminatesOn?: string | null;
  sectionAffiliation?: TimelineSectionAffiliation | null;
  section?: TimelineSection | null;
  programType?: {
    id: string;
    name: string;
  } | null;
  classification?: {
    id: string;
    name: string;
    description?: string | null;
  } | null;
};

export type TimelineSectionAffiliation = {
  id: string;
  primary: boolean;
  startAt: string;
  endAt?: string | null;
  section?: TimelineSection | null;
};

type TimelineSection = {
  id: string;
  name: string;
  legacyId?: string | null;
};

export type TimelineEmployment = {
  id: string;
  endedOn?: string | null;
  isPrimary: boolean;
  jobClassification?: string | null;
  startedOn: string | null;
  title?: string | null;
  formattedTitle?: string | null;
  facility?: {
    id: string;
    name: string;
    section?: TimelineSection | null;
  } | null;
};

export type TimelineCcmsSuspension = {
  id: string;
  code: string;
  startDate: string;
  endDate?: string | null;
  reasonDescription?: string | null;
  memo?: string | null;
};

export type TimelineProspectProgram = {
  id: string;
  backgroundCheck?: TimelineStatusWithDates | null;
  playingAbilityTestStatus?: {
    startDate?: string | null;
    expirationDate?: string | null;
    status?: string | null;
    submissions?: Array<{
      id?: string | null;
      patType?: string | null;
      date: string;
      expirationDate?: string | null;
      result?: string | null;
    }> | null;
  } | null;
  qualifyingLevel?: (TimelineStatusWithDates & { completed?: boolean | null }) | null;
  membershipInterestForm?: {
    id: string;
    startedAt: string;
    reasonForBecomingMember?: string | null;
  } | null;
  eligibleEmployment?: {
    id: string;
    startDate?: string | null;
    endDate?: string | null;
  } | null;
};

type TimelineStatusWithDates = {
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  expirationDate?: string | null;
};

export type TimelineEducationCertification = {
  startDate: string;
  endDate?: string | null;
  programDescription: string;
  careerPath?: string | null;
};

export type TimelineEducationPgmProgram = {
  curriculumVersion: string;
  startDate: string;
  endDate?: string | null;
  level: number;
  levelCode: string;
  careerPath?: string | null;
  programType?: string | null;
  completed: boolean;
  testHistory?: Array<{
    attemptNumber: number;
    completionStatus?: string | null;
    testDate: string;
    testDefinition?: {
      description?: string | null;
    } | null;
  }> | null;
};

export const PERSON_TIMELINE_QUERY: TypedDocumentNode<PersonTimelineData, PersonTimelineQueryVariables> = gql`
  query GetPersonTimeline($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      accountCreatedAt
      bands {
        id
        createdReason
        terminatedReason
        expiresOn
        startedOn
        terminatesOn
        sectionAffiliation {
          id
          primary
          section {
            id
            name
            legacyId
          }
        }
        section {
          id
          name
          legacyId
        }
        programType {
          id
          name
        }
        classification {
          id
          name
          description
        }
      }
      sectionAffiliations {
        id
        primary
        startAt
        endAt
        section {
          id
          name
          legacyId
        }
      }
      employments {
        id
        endedOn
        isPrimary
        jobClassification
        startedOn
        title
        formattedTitle
        facility {
          id
          name
          section {
            id
            name
            legacyId
          }
        }
      }
      ccmsSuspensions {
        id
        code
        startDate
        endDate
        reasonDescription
        memo
      }
      prospectProgram {
        id
        backgroundCheck {
          status
          startDate: screenedAt
          expirationDate: expiredAt
        }
        playingAbilityTestStatus {
          startDate
          expirationDate
          status
          submissions {
            id
            patType
            date
            expirationDate
            result
          }
        }
        qualifyingLevel {
          status
          startDate
          expirationDate: expiredAt
          endDate: completedAt
          completed
        }
        membershipInterestForm {
          id
          startedAt
          reasonForBecomingMember
        }
        eligibleEmployment {
          id
          startDate: startedOn
          endDate: endedOn
        }
      }
      educationLatestCertification {
        startDate
        endDate
        programDescription
        careerPath
      }
      educationPgmProgramHistory {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        testHistory {
          attemptNumber
          completionStatus
          testDate
          testDefinition {
            description
          }
        }
      }
    }
  }
`;
